import { BarChart, LineChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { type ReactNode, useEffect, useRef } from "react";

import { COLORS } from "../tokens";

// Register only what the command-center needs from echarts/core (tree-shakeable). Apps that want
// more can register additional charts before first render.
echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

export type EChartOption = echarts.EChartsCoreOption;

export interface EChartProps {
  readonly option: EChartOption;
  readonly height?: number;
}

const DEFAULT_HEIGHT = 280;

// Dark-themed defaults merged under every option so individual charts stay terse and on-brand.
const darkBase: EChartOption = {
  backgroundColor: "transparent",
  textStyle: { color: COLORS.textSecondary },
  grid: { borderColor: COLORS.border },
};

// Minimal typed ECharts base: mounts an echarts/core instance, applies dark defaults, resizes with
// its container, and disposes cleanly. The real chart definitions live in the app, passed as
// `option`.
export const EChart = ({ option, height = DEFAULT_HEIGHT }: EChartProps): ReactNode => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const chart = echarts.init(el, undefined, { renderer: "canvas" });
    chart.setOption({ ...darkBase, ...option });

    const onResize = (): void => chart.resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [option]);

  return <div ref={ref} style={{ width: "100%", height }} />;
};
